import { create } from 'zustand'
import { ContactListItem, Scope } from '@utils'
import { useContactMappingStore } from './useContactMappingStore'

export interface ConversationMessage {
  id: string
  text: string
  timestamp: string
  image?: string
  isSent: boolean
  isPinned?: boolean
  senderId: string
  status?: 'sent' | 'delivered' | 'read'
}

export type ConversationParticipant = {
  id: string
  name: string
  avatar: any
  isOnline?: boolean
}

export interface Conversation {
  id: string
  type: Scope // Use Scope for type
  name?: string
  participants: ConversationParticipant[]
  lastMessage: string
  lastMessageTime: string
  messages: ConversationMessage[]
}

type MessagesStore = {
  conversations: Conversation[]
  me: ConversationParticipant
  addOrUpdateConversation: (conversation: Omit<Conversation, 'lastMessage' | 'lastMessageTime' | 'messages'>, message?: ConversationMessage) => void
  addMessageToConversation: (conversationId: string, message: ConversationMessage) => void
  removeMember: (conversationId: string, memberId: string) => void
  prepareConversation: (participant: ContactListItem[], groupName?: string) => Conversation
  togglePin: (conversationId: string, messageId: string) => void
}

// Example users from contacts
const userMe: ConversationParticipant = {
  id: 'me',
  name: 'Louise Dupond',
  avatar: 'https://randomuser.me/api/portraits/men/99.jpg',
  isOnline: true,
}
const user1: ConversationParticipant = {
  id: '1',
  name: 'Olivier Dubois',
  avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
  isOnline: true,
}
const user2: ConversationParticipant = {
  id: '2',
  name: 'Olivier Martin',
  avatar: 'https://randomuser.me/api/portraits/men/2.jpg',
  isOnline: false,
}
const user3: ConversationParticipant = {
  id: '3',
  name: 'Pierre Bernard',
  avatar: 'https://randomuser.me/api/portraits/men/3.jpg',
  isOnline: true,
}
const user4: ConversationParticipant = {
  id: '4',
  name: 'Marie Olivier',
  avatar: 'https://randomuser.me/api/portraits/women/1.jpg',
  isOnline: false,
}

const now = new Date()
const time = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

const defaultConversations: Conversation[] = [
  {
    id: 'conv-1',
    type: Scope.PRIVATE,
    participants: [userMe, user1],
    lastMessage: 'See you soon!',
    lastMessageTime: time,
    messages: [
      {
        id: 'msg-1',
        text: 'Hi Olivier!',
        timestamp: time,
        isSent: true,
        senderId: 'me',
        status: 'read',
      },
      {
        id: 'msg-2',
        text: 'Hello! How are you?',
        timestamp: time,
        isSent: false,
        senderId: '1',
        status: 'read',
      },
      {
        id: 'msg-3',
        text: 'See you soon!',
        timestamp: time,
        isSent: true,
        senderId: 'me',
        status: 'sent',
      },
    ],
  },
  {
    id: 'conv-2',
    type: Scope.PROFESSIONAL,
    participants: [userMe, user2],
    lastMessage: 'Let’s catch up later.',
    lastMessageTime: time,
    messages: [
      {
        id: 'msg-4',
        text: 'Hey Olivier Martin!',
        timestamp: time,
        isSent: true,
        senderId: 'me',
        status: 'read',
      },
      {
        id: 'msg-5',
        text: 'Hi! Let’s catch up later.',
        timestamp: time,
        isSent: false,
        senderId: '2',
        status: 'sent',
      },
    ],
  },
  {
    id: 'conv-me-1-2-3-4',
    type: Scope.GROUP,
    name: 'Friends',
    participants: [userMe, user1, user2, user3, user4],
    lastMessage: 'Group meeting at 5pm!',
    lastMessageTime: time,
    messages: [
      {
        id: 'msg-6',
        text: 'Hello group!',
        timestamp: time,
        isSent: true,
        senderId: 'me',
        status: 'read',
      },
      {
        id: 'msg-7',
        text: 'Hi everyone!',
        timestamp: time,
        isSent: false,
        senderId: '1',
        status: 'read',
      },
      {
        id: 'msg-8',
        text: 'Group meeting at 5pm!',
        timestamp: time,
        isSent: false,
        senderId: '3',
        status: 'sent',
      },
    ],
  },
]

export const useMessagesStore = create<MessagesStore>((set, get) => ({
  me: userMe,
  conversations: defaultConversations,
  addOrUpdateConversation: (conversation, message) =>
    set((state) => {
      const now = new Date()
      const time = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      const existing = state.conversations.find((c) => c.id === conversation.id)
      if (existing) {
        // Add message and update last message/time
        return {
          conversations: state.conversations.map((c) =>
            c.id === conversation.id
              ? {
                  ...c,
                  lastMessage: message?.text ?? c.lastMessage,
                  lastMessageTime: message ? time : c.lastMessageTime,
                  messages: message ? [...c.messages, message] : c.messages,
                }
              : c
          ),
        }
      } else {
        // Add new conversation
        return {
          conversations: [
            {
              ...conversation,
              lastMessage: message?.text ?? '',
              lastMessageTime: message ? time : '',
              messages: message ? [message] : [],
            },
            ...state.conversations,
          ],
        }
      }
    }),
  addMessageToConversation: (conversationId, message) =>
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === conversationId
          ? {
              ...c,
              lastMessage: message?.text,
              lastMessageTime: message.timestamp,
              messages: [...c.messages, message],
            }
          : c
      ),
    })),
    prepareConversation: (participant: ContactListItem[], groupName?: string) => {
      const conversationId = `conv-${participant.filter(p => p.id !== 'me').map(p => p.id).join('-')}`
      const conversation = get().conversations.find(c => c.id === conversationId)
      if (conversation) {
        return conversation
      }
      const participants = participant.map(p => ({
        id: p.id ?? '',
        name: p.fullName ?? `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
        avatar: p.avatarUrl ?? '',
        isOnline: false,
      }))
      const getScope  = useContactMappingStore.getState().getScope
      return {
        id: conversationId,
        name: groupName,
        type: participant.length === 1 ? (getScope(participant[0].id).includes(Scope.PRIVATE) ? Scope.PRIVATE : Scope.PROFESSIONAL) : Scope.GROUP,
        participants: [userMe, ...participants],
        lastMessage: '',
        lastMessageTime: '',
        messages: [],
      }
    },
    removeMember: (conversationId: string, memberId: string) => {
      set((state) => ({
        conversations: state.conversations.map((c) =>
          c.id === conversationId
            ? {
                ...c,
                participants: c.participants.filter(p => p.id !== memberId)
              }
            : c
        )
      }))
    },
    togglePin: (conversationId: string, messageId: string) => 
      set((state) => ({
        conversations: state.conversations.map((c) =>
          c.id === conversationId
            ? {
                ...c,
                messages: c.messages.map(m => m.id === messageId? {...m, isPinned: !m.isPinned} : m),
              }
            : c
        )
      }))
}))